import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, Instagram, Facebook, Linkedin, Youtube, TrendingUp, Users, Clock, Award, MessageCircle, BarChart3 } from "lucide-react";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { EnhancedStructuredData } from "@/components/EnhancedStructuredData";
import { SEOArticleStructure } from "@/components/SEOArticleStructure";
import { useSEOOptimization } from "@/hooks/useSEOOptimization";
import { generateCanonicalUrl } from "@/utils/seoHelpers";

const SocialMediaTheWoodlandsPage = () => {
  const [language, setLanguage] = useState<'es' | 'en'>('es');
  const toggleLanguage = () => setLanguage(language === 'es' ? 'en' : 'es');
  
  useSEOOptimization();
  const canonicalUrl = generateCanonicalUrl('/social-media-marketing-the-woodlands');

  const services = [
    {
      icon: MessageCircle,
      title: "Estrategia de Contenido Social",
      description: "Calendario editorial, voz de marca y storytelling adaptado a audiencia local/hispana."
    },
    {
      icon: Instagram,
      title: "Gestión de Cuentas Sociales",
      description: "Publicaciones regulares, diseño optimizado, reels/stories e interacción con comunidad."
    },
    {
      icon: TrendingUp,
      title: "Publicidad en Redes Sociales",
      description: "Campañas en Facebook, Instagram, LinkedIn, YouTube con segmentación bilingüe."
    },
    {
      icon: Users,
      title: "Crecimiento de Comunidad",
      description: "Seguidores cualificados, engagement, reputación e influencers locales/hispanos."
    },
    {
      icon: BarChart3,
      title: "Tracking y Conversión",
      description: "Integración con web, CRM y medición de atribución completa."
    },
    {
      icon: Award,
      title: "Informes Transparentes",
      description: "Métricas mensuales: alcance, engagement, conversiones y ROI social."
    }
  ];

  const process = [
    {
      day: "Día 0-10",
      title: "Auditoría & KPIs",
      description: "Análisis de redes actuales, definición de objetivos y audiencia hispana/inglés."
    },
    {
      day: "Día 11-30",
      title: "Estrategia & Lanzamiento",
      description: "Diseño de contenido, configuración de campañas y publicaciones iniciales."
    },
    {
      day: "Día 31-60",
      title: "Optimización & Pruebas",
      description: "A/B testing de creativos, contenido dinámico (vídeo, reels) y seguimiento."
    },
    {
      day: "Día 61-90",
      title: "Escalado & Análisis",
      description: "Crecimiento comunitario, integración SEO/PPC y plan de 6-12 meses."
    }
  ];

  const results = [
    "Mayor visibilidad y engagement de tu marca en redes sociales locales + hispanas",
    "Seguidores cualificados que se convierten en leads o clientes reales",
    "Campañas pagas optimizadas que reducen CPA y mejoran ROAS desde redes",
    "Integración de social en el funnel de marketing con resultados de negocio",
    "Cultura de marca bilingüe con resonancia en audiencia hispana/inglés en Texas"
  ];

  const sectors = [
    "Servicios Locales Bilingües (HVAC, Remodelación, Dental)",
    "E-commerce Bilingüe",
    "Restaurantes/Bares (Mercado Hispano/Inglés)",
    "Profesionales B2B con LinkedIn",
    "Startups Hispano/Inglés en Texas"
  ];

  const plans = [
    {
      name: "Plan Social Básico",
      duration: "Mensual",
      description: "Presencia profesional en redes sociales con gestión consistente.",
      features: [
        "Gestión de 2 plataformas",
        "8-12 publicaciones/mes",
        "Reportes mensuales",
        "Interacción básica comunidad"
      ]
    },
    {
      name: "Plan Social Growth",
      duration: "Mensual",
      description: "Crecimiento acelerado con contenido premium y campañas pagadas.",
      features: [
        "3 plataformas activas",
        "Anuncios pagados incluidos",
        "Contenido de vídeo/reels",
        "Crecimiento de comunidad"
      ]
    },
    {
      name: "Plan Social Suite Premium",
      duration: "Mensual",
      description: "Solución completa bilingüe con máxima integración y resultados.",
      features: [
        "Estrategia bilingüe ES/EN",
        "4+ plataformas",
        "Campañas pagadas avanzadas",
        "Integración con SEO/PPC"
      ]
    }
  ];

  const faqs = [
    {
      question: "¿Cuáles redes sociales debo usar para mi negocio?",
      answer: "Depende de tu audiencia: en The Woodlands los segmentos hispano/inglés responden bien a Instagram, Facebook y YouTube; para B2B LinkedIn funciona mejor. Evaluamos tu caso y recomendamos las plataformas más efectivas."
    },
    {
      question: "¿Necesito publicar todos los días?",
      answer: "No necesariamente — lo importante es consistencia + calidad + interacción. Publicamos con estrategia pensada para tu audiencia, no solo volumen por volumen."
    },
    {
      question: "¿Cómo medimos conversión desde redes sociales?",
      answer: "Integrando redes con tu sitio web y CRM: medimos clics, formularios, llamadas, ventas y asignamos atribución específica a cada canal social."
    },
    {
      question: "¿Cuánto tiempo hasta ver resultados?",
      answer: "Normalmente empiezas a ver crecimiento de comunidad y engagement en 30–60 días; conversión efectiva a leads/ventas entre 90 días y 6 meses con optimización continua."
    }
  ];

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>Agencia de Social Media en The Woodlands | Gestión y Publicidad Bilingüe – Luis ROC</title>
        <meta 
          name="description" 
          content="Impulsa tu marca en redes sociales desde The Woodlands con contenido bilingüe, campañas pagadas y comunidad activa. Gestión completa de Instagram, Facebook, LinkedIn + análisis de resultados con Luis ROC." 
        />
        <meta name="keywords" content="social media the woodlands, marketing redes sociales, gestión instagram, publicidad facebook, social media bilingüe, community management" />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content="Agencia de Social Media en The Woodlands | Luis ROC" />
        <meta property="og:description" content="Gestión y publicidad bilingüe en redes sociales. Construye comunidad y convierte." />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="es_US" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Social Media Marketing The Woodlands | Luis ROC" />
        <meta name="twitter:description" content="Gestión bilingüe de redes sociales que genera resultados." />
      </Helmet>

      <EnhancedStructuredData
        service={{
          name: "Social Media Marketing The Woodlands",
          description: "Servicios profesionales de gestión y publicidad en redes sociales para empresas en The Woodlands, Texas",
          provider: "Luis ROC",
          areaServed: ["The Woodlands", "Houston", "Spring", "Conroe", "Montgomery County", "Texas"],
        }}
        faqs={faqs}
        webpage={{
          name: "Social Media Marketing en The Woodlands",
          description: "Servicios profesionales de social media marketing en The Woodlands, TX. Gestión bilingüe y campañas que convierten.",
          url: canonicalUrl,
          keywords: ["social media marketing", "gestión redes sociales", "publicidad facebook", "instagram marketing"]
        }}
      />

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={toggleLanguage} />
        
        <main>
          <SEOArticleStructure
            title="Social Media Marketing en The Woodlands para marcas que quieren comunidad y conversiones"
            description="Servicios profesionales de social media marketing. Gestión bilingüe de redes sociales y campañas pagadas."
            author="Luis ROC"
            keywords={["social media marketing", "gestión redes sociales", "publicidad social"]}
            className="container mx-auto px-4 py-8"
          >
            {/* Breadcrumb Navigation */}
            <BreadcrumbNav 
              items={[
                { name: "Servicios", url: "/servicios" },
                { name: "Social Media Marketing", url: "/social-media-marketing-the-woodlands" }
              ]}
              language="es"
            />

            {/* Hero Section */}
            <section className="py-16 text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
                Social Media Marketing en The Woodlands para marcas que quieren comunidad y conversiones
              </h1>
              
              <div className="max-w-4xl mx-auto mb-8">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  En Luis ROC, entendemos que las redes sociales no solo sirven para "tener presencia", sino para construir una 
                  comunidad activa y convertir seguidores en clientes. Desde nuestra sede en The Woodlands, ofrecemos un servicio 
                  bilingüe (inglés/español) que combina contenido creativo, anuncios sociales altamente segmentados y analítica de 
                  conversión. Gestionamos tus redes (Instagram, Facebook, LinkedIn, YouTube) como si fueran parte de tu equipo interno: 
                  construimos la estrategia, creamos el contenido, lanzamos campañas pagadas, y medimos resultados con foco en tráfico, 
                  leads y ventas. Si estás listo para llevar tu marca al siguiente nivel en redes sociales, hablemos.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  onClick={() => window.location.href = '/contacto'}
                  className="text-lg px-8"
                >
                  Agenda tu reunión inicial gratuita de 30 minutos
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => window.location.href = '/casos-de-exito'}
                  className="text-lg px-8"
                >
                  Ver Casos de Éxito
                </Button>
              </div>
            </section>

            {/* Social Platforms Badges */}
            <section className="py-8 mb-8">
              <div className="flex flex-wrap justify-center gap-6">
                <div className="flex items-center gap-2 px-6 py-3 bg-accent rounded-full">
                  <Instagram className="h-6 w-6" />
                  <span className="font-semibold">Instagram</span>
                </div>
                <div className="flex items-center gap-2 px-6 py-3 bg-accent rounded-full">
                  <Facebook className="h-6 w-6" />
                  <span className="font-semibold">Facebook</span>
                </div>
                <div className="flex items-center gap-2 px-6 py-3 bg-accent rounded-full">
                  <Linkedin className="h-6 w-6" />
                  <span className="font-semibold">LinkedIn</span>
                </div>
                <div className="flex items-center gap-2 px-6 py-3 bg-accent rounded-full">
                  <Youtube className="h-6 w-6" />
                  <span className="font-semibold">YouTube</span>
                </div>
              </div>
            </section>

            {/* Services Section */}
            <section className="py-16 bg-accent/5 rounded-lg mb-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                  Nuestros servicios de Social Media
                </h2>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {services.map((service, index) => {
                    const Icon = service.icon;
                    return (
                      <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                        <Icon className="h-12 w-12 text-primary mb-4" />
                        <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                        <p className="text-muted-foreground">{service.description}</p>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Process Section */}
            <section className="py-16 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                Nuestro proceso en 90 días
              </h2>
              <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                Entregamos calendario/social dashboard + reporte de métricas + recomendaciones
              </p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {process.map((step, index) => (
                  <Card key={index} className="p-6 relative">
                    <div className="absolute -top-4 left-6 bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div className="flex items-center gap-2 mb-3 mt-2">
                      <Clock className="h-5 w-5 text-primary" />
                      <span className="font-semibold text-primary">{step.day}</span>
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </Card>
                ))}
              </div>
            </section>

            {/* Results Section */}
            <section className="py-16 bg-primary/5 rounded-lg mb-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                  Resultados que puedes esperar
                </h2>
                
                <div className="max-w-3xl mx-auto space-y-4">
                  {results.map((result, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-background rounded-lg">
                      <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <p className="text-lg">{result}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Sectors Section */}
            <section className="py-16 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                Sectores que servimos
              </h2>
              <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                Especialistas en social media para negocios locales y mercados bilingües
              </p>
              
              <div className="flex flex-wrap justify-center gap-4">
                {sectors.map((sector, index) => (
                  <div 
                    key={index}
                    className="px-6 py-3 bg-accent text-accent-foreground rounded-full font-medium hover:bg-accent/80 transition-colors"
                  >
                    {sector}
                  </div>
                ))}
              </div>
            </section>

            {/* Plans Section */}
            <section className="py-16 bg-accent/5 rounded-lg mb-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                  Planes y precios
                </h2>
                <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                  Solicita propuesta personalizada según tu audiencia, plataformas y presupuesto
                </p>
                
                <div className="grid md:grid-cols-3 gap-8">
                  {plans.map((plan, index) => (
                    <Card key={index} className="p-8 hover:shadow-xl transition-shadow">
                      <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                      <p className="text-primary font-semibold mb-4">{plan.duration}</p>
                      <p className="text-muted-foreground mb-6">{plan.description}</p>
                      
                      <ul className="space-y-3 mb-6">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <Button 
                        className="w-full"
                        onClick={() => window.location.href = '/contacto'}
                      >
                        Solicitar Propuesta
                      </Button>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Preguntas Frecuentes
              </h2>
              
              <div className="max-w-3xl mx-auto space-y-6">
                {faqs.map((faq, index) => (
                  <Card key={index} className="p-6">
                    <h3 className="text-xl font-semibold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </Card>
                ))}
              </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground rounded-lg text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Construye tu Comunidad y Aumenta tus Conversiones en Redes
              </h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
                reunión inicial gratuita de 30 minutos para revisar el contexto de tus redes y definir posibles siguientes pasos
              </p>
              <Button 
                size="lg"
                variant="secondary"
                onClick={() => window.location.href = '/contacto'}
                className="text-lg px-8"
              >
                Agenda tu reunión inicial gratuita de 30 minutos
              </Button>
            </section>

            {/* Internal Links Section */}
            <section className="py-16">
              <h2 className="text-2xl font-bold mb-6 text-center">Servicios Complementarios</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold mb-2">SEO The Woodlands</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Combina social media con SEO para máximo alcance
                  </p>
                  <Button variant="link" onClick={() => window.location.href = '/seo-the-woodlands'}>
                    Ver SEO Local →
                  </Button>
                </Card>
                
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold mb-2">Google Ads The Woodlands</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Integra campañas sociales con Google Ads
                  </p>
                  <Button variant="link" onClick={() => window.location.href = '/google-ads-the-woodlands'}>
                    Ver PPC →
                  </Button>
                </Card>
                
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold mb-2">Branding & Web</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Refuerza tu identidad visual en redes
                  </p>
                  <Button variant="link" onClick={() => window.location.href = '/branding-web-the-woodlands'}>
                    Ver Branding →
                  </Button>
                </Card>
              </div>
            </section>
          </SEOArticleStructure>
        </main>

        <KnowledgeConnections path="/social-media-marketing-the-woodlands" language={language} />

        <Footer language={language} />
      </div>
    </>
  );
};

export default SocialMediaTheWoodlandsPage;
